import {trackEvent} from './TrackingV2';

/**
 * Nova task agent analytics sent from the browser: a person seeing a run's reply, and copying it.
 * The run events themselves are sent by graphql; these carry the same `runId`, so a chart can
 * follow one run from start to copy. Ids and the outcome only - never an answer, message or name.
 *
 * The object name is locked: renaming an event loses its Amplitude history.
 */
const NOVA_TASK_AGENT_OUTPUT = 'Nova Task Agent Output';

type NovaOutcome = 'Succeeded' | 'Failed';

// Only a finished run's reply is something to have seen. A stopped run's reply is never shown, and
// anything else is still running.
export const toNovaOutcome = (outcome: string | null): NovaOutcome | null => {
	if (outcome === 'SUCCEEDED') return 'Succeeded';
	if (outcome === 'FAILED') return 'Failed';
	return null;
};

// The browser holds a task's Relay global id (base64 of `Task:<id>`); graphql sends the numeric id
// with the run events, so the same task carries the same value on all four. Anything that is not a
// task global id gives null and no event, so taskId is always a number in Amplitude.
const toTrackedTaskId = (taskId: string): number | null => {
	try {
		const match = /^Task:(\d+)$/.exec(atob(taskId));
		return match ? parseInt(match[1], 10) : null;
	} catch (_notBase64) {
		return null;
	}
};

// Runs whose Viewed has been sent in this page load. A reload starts again, which is what makes a
// later visit count as another view.
const viewedRunIds = new Set<string>();

// Lets a watcher stop watching a reply whose view has already been sent.
export const hasNovaOutputBeenViewed = (runId: string): boolean => viewedRunIds.has(runId);

export const resetNovaOutputViewedForTests = (): void => {
	viewedRunIds.clear();
};

export const trackNovaOutputViewed = ({
	runId,
	taskId,
	outcome,
}: {
	runId: string | null;
	taskId: string;
	outcome: string | null;
}): void => {
	try {
		const novaOutcome = toNovaOutcome(outcome);
		const trackedTaskId = toTrackedTaskId(taskId);
		if (!runId || !novaOutcome || trackedTaskId === null || viewedRunIds.has(runId)) return;
		// Marked whether or not the send succeeded: a view is reported once, never retried.
		viewedRunIds.add(runId);
		trackEvent(NOVA_TASK_AGENT_OUTPUT, 'Viewed', {runId, taskId: trackedTaskId, outcome: novaOutcome});
	} catch (_ignored) {
		// Tracking must never break the thread it observes.
	}
};

// Every click counts: each copy is a use of the output.
export const trackNovaOutputCopied = ({runId, taskId}: {runId: string | null; taskId: string}): void => {
	try {
		const trackedTaskId = toTrackedTaskId(taskId);
		if (!runId || trackedTaskId === null) return;
		trackEvent(NOVA_TASK_AGENT_OUTPUT, 'Copied', {runId, taskId: trackedTaskId});
	} catch (_ignored) {
		// Tracking must never break the copy it observes.
	}
};
